import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { openTab } from "../redux/actions/workspaceActions";
import {
  SIDEBAR_ITEMS,
  SidebarItem,
  SidebarEntry,
} from "../config/sidebarConfig";
import { STANDALONE_ROUTES } from "../config/standAlonePagesRoutes";

const isSidebarItem = (entry: SidebarEntry): entry is SidebarItem => {
  return !("isHeader" in entry);
};

const useRouteToWorkspaceSync = (): void => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { tabs } = useAppSelector((state) => state.workspace);

  useEffect(() => {
    let currentPath = location.pathname;

    // 1. Force route to /dashboard if we are at the root
    if (currentPath === "/" || currentPath === "") {
      currentPath = "/dashboard";
      navigate(currentPath, { replace: true });
    }

    // Ignore standalone pages completely
    if (STANDALONE_ROUTES.some((route) => currentPath.startsWith(route)))
      return;

    // 2. If no tabs are open (e.g., just logged in or hard refresh)
    if (tabs.length === 0) {
      // Find the corresponding item in the sidebar
      const item = SIDEBAR_ITEMS.filter(isSidebarItem).find(
        (i) => i.path === currentPath,
      );

      if (item && item.componentKey) {
        dispatch(
          openTab({
            id: item.path,
            title: item.title,
            path: item.path,
            componentKey: item.componentKey,
          }),
        );
      } else {
        // Fallback for Dynamic Routes syncing on Hard Refresh
        const parts = currentPath.split("/");

        if (currentPath === "/purchase-orders/create") {
          dispatch(
            openTab({
              id: currentPath,
              title: "New Purchase Order",
              path: currentPath,
              componentKey: "PURCHASE_ORDER_CREATE",
            }),
          );
        } else if (parts.length >= 3) {
          const id = parts[2];
          const shortId = id.substring(0, 8);

          if (currentPath.startsWith("/student-fee-ledgers/"))
            dispatch(
              openTab({
                id: currentPath,
                title: `Ledger #${shortId}...`,
                path: currentPath,
                componentKey: "STUDENT_FEE_LEDGERS",
              }),
            );
          else if (currentPath.startsWith("/purchase-orders/"))
            dispatch(
              openTab({
                id: currentPath,
                title: `P.Order #${shortId}...`,
                path: currentPath,
                componentKey: "PURCHASE_ORDER",
              }),
            );
        } else if (currentPath === "/dashboard") {
          dispatch(
            openTab({
              id: "/dashboard",
              title: "Dashboard",
              path: "/dashboard",
              componentKey: "DASHBOARD",
            }),
          );
        }
      }
    }
  }, [location.pathname, tabs.length, dispatch, navigate]);
};

export default useRouteToWorkspaceSync;
